import React, { useEffect, useRef } from 'react';
import {
  ActivityIndicator,
  Animated,
  Dimensions,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { COLORS } from '../constants/theme';
import { FilterSection } from './FilterSection';

const { width } = Dimensions.get('window');

interface FilterOption {
  value: string;
  label: string;
}

interface FiltersModalProps {
  visible: boolean;
  onClose: () => void;
  productTypes: FilterOption[];
  issuers: FilterOption[];
  reports: FilterOption[];
  selectedProductTypes: string[];
  selectedIssuers: string[];
  selectedReports: string[];
  onProductTypesChange: (values: string[]) => void;
  onIssuersChange: (values: string[]) => void;
  onReportsChange: (values: string[]) => void;
  loading?: boolean;
}

export const FiltersModal: React.FC<FiltersModalProps> = ({
  visible,
  onClose,
  productTypes,
  issuers,
  reports,
  selectedProductTypes,
  selectedIssuers,
  selectedReports,
  onProductTypesChange,
  onIssuersChange,
  onReportsChange,
  loading = false,
}) => {
  const isTabletOrDesktop = width >= 768;
  const slideAnim = useRef(new Animated.Value(width)).current;

  useEffect(() => {
    if (visible) {
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 65,
        friction: 11,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: width,
        duration: 250,
        useNativeDriver: true,
      }).start();
    }
  }, [visible, slideAnim]);

  return (
    <Modal visible={visible} animationType='none' transparent={true} onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <TouchableOpacity style={styles.modalBackdrop} activeOpacity={1} onPress={onClose} />
        <Animated.View
          style={[
            styles.modalContent,
            isTabletOrDesktop && styles.modalContentDesktop,
            { transform: [{ translateX: slideAnim }] },
          ]}
        >
          {/* Header */}
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Filters</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          {/* Filters Content */}
          <ScrollView
            style={styles.filtersScrollView}
            contentContainerStyle={styles.filtersContent}
            showsVerticalScrollIndicator={false}
          >
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size='large' color={COLORS.primary} />
                <Text style={styles.loadingText}>Loading filters...</Text>
              </View>
            ) : (
              <>
                {productTypes.length > 0 && (
                  <FilterSection
                    title='Product Code'
                    options={productTypes}
                    selectedValues={selectedProductTypes}
                    onSelectionChange={onProductTypesChange}
                  />
                )}
                {issuers.length > 0 && (
                  <FilterSection
                    title='Issuer'
                    options={issuers}
                    selectedValues={selectedIssuers}
                    onSelectionChange={onIssuersChange}
                  />
                )}
                {reports.length > 0 && (
                  <FilterSection
                    title='Portfolio'
                    options={reports}
                    selectedValues={selectedReports}
                    onSelectionChange={onReportsChange}
                    singleSelect
                  />
                )}
                {!loading &&
                  productTypes.length === 0 &&
                  issuers.length === 0 &&
                  reports.length === 0 && (
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>No filters available</Text>
                  </View>
                )}
              </>
            )}
          </ScrollView>

          {/* Footer with Apply Button */}
          <View style={styles.modalFooter}>
            <TouchableOpacity style={styles.applyButton} onPress={onClose}>
              <Text style={styles.applyButtonText}>Apply Filters</Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalContent: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: width >= 768 ? 400 : width * 0.85,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderBottomLeftRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: -2, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 10,
  },
  modalContentDesktop: {
    width: 400,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1F2937',
    fontFamily: 'Montserrat_600SemiBold',
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonText: {
    fontSize: 20,
    color: '#6B7280',
    fontWeight: '600',
  },
  filtersScrollView: {
    flex: 1,
  },
  filtersContent: {
    padding: 16,
  },
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#6B7280',
    fontFamily: 'Montserrat_400Regular',
  },
  emptyContainer: {
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: '#9CA3AF',
    fontFamily: 'Montserrat_400Regular',
  },
  modalFooter: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  applyButton: {
    backgroundColor: COLORS.primary || '#3B82F6',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  applyButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    fontFamily: 'Montserrat_600SemiBold',
  },
});
